/**
 * 订单匹配工具函数
 * 从 SplitRouteDrawer 抽离，用于根据分单规则（多边形/邮编/stop范围）匹配当前实时订单列表中的 orderIds
 * SplitOrderPage 和 SplitRouteDrawer 共用，保持里外匹配逻辑一致
 */

export interface OrderMatchItem {
    orderId: number | null;
    latitude: number | null;
    longitude: number | null;
    zipcode: string | null;
    stopNumber: number | null;
}

export interface StopRange {
    id: string;
    parcelNumberStart: string;
    parcelNumberEnd: string;
}

/**
 * 点是否在多边形内
 * 优先用 Google geometry 库（球面几何，精确），降级用射线投射算法
 */
const isPointInPolygon = (point: { lat: number; lng: number }, polygon: { lat: number; lng: number }[]): boolean => {
    if (typeof google !== 'undefined' && google.maps?.geometry?.poly?.containsLocation) {
        const gmPoly = new google.maps.Polygon({ paths: polygon });
        return google.maps.geometry.poly.containsLocation(
            new google.maps.LatLng(point.lat, point.lng),
            gmPoly,
        );
    }
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i].lng, yi = polygon[i].lat;
        const xj = polygon[j].lng, yj = polygon[j].lat;
        if (((yi > point.lat) !== (yj > point.lat)) &&
            (point.lng < (xj - xi) * (point.lat - yi) / (yj - yi) + xi)) {
            inside = !inside;
        }
    }
    return inside;
};

/**
 * 根据多边形从 orderList 匹配 orderIds
 */
export const matchOrderIdsByPolygons = (
    orderList: OrderMatchItem[],
    polygons: { lat: number; lng: number }[][],
    excludeIds?: Set<number>,
): number[] =>
    orderList.filter(o => {
        if (o.orderId == null || o.latitude == null || o.longitude == null) return false;
        if (excludeIds?.has(Number(o.orderId))) return false;
        return polygons.some(poly => poly.length >= 3 && isPointInPolygon({ lat: o.latitude!, lng: o.longitude! }, poly));
    }).map(o => Number(o.orderId));

/**
 * 根据邮编列表从 orderList 匹配 orderIds
 */
export const matchOrderIdsByZipcodes = (
    orderList: OrderMatchItem[],
    zipcodes: string[],
): number[] =>
    orderList.filter(o => {
        if (o.orderId == null || o.zipcode == null) return false;
        return zipcodes.includes(o.zipcode);
    }).map(o => Number(o.orderId));

/**
 * 根据 stopRanges 从 orderList 匹配 orderIds
 */
export const matchOrderIdsByStopRanges = (
    orderList: OrderMatchItem[],
    ranges: StopRange[],
): number[] =>
    orderList.filter(o => {
        if (o.orderId == null || o.stopNumber == null) return false;
        return ranges.some(r => {
            const start = Number(r.parcelNumberStart), end = Number(r.parcelNumberEnd);
            return r.parcelNumberStart && r.parcelNumberEnd && o.stopNumber! >= start && o.stopNumber! <= end;
        });
    }).map(o => Number(o.orderId));